import React, { useState, useEffect } from 'react';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmSelector from '../components/FarmSelector';
import {
  CalendarDaysIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import FarmSearchDropdown from '../components/FarmSearchDropdown';

const PlantingSchedule = () => {
  const { farms, selectedFarm, selectedFarmId, loading: farmLoading, isAdmin, hasOrganizations, hasFarms } = useFarm();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, tomorrow, upcoming, past
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');

  // Keep the local Filters-card farm dropdown in sync with the top-right
  // FarmSelector. Without this sync the selector at the top doesn't filter
  // the page at all — users perceive that as a data leak.
  useEffect(() => {
    if (selectedFarmId && selectedFarmId !== 'all') {
      setSelectedFarmFilter(String(selectedFarmId));
    } else if (selectedFarmId === 'all') {
      setSelectedFarmFilter('all');
    }
  }, [selectedFarmId]);
  const [selectedProcess, setSelectedProcess] = useState('all');
  const [processes, setProcesses] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    farms: 0,
    completed: 0,
    pending: 0
  });
  const [dateRange, setDateRange] = useState({
    start: '2024-01-01',
    end: new Date().toISOString().split('T')[0]
  });
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 100;

  // Load processes for filter
  useEffect(() => {
    const loadProcesses = async () => {
      const { data } = await supabase
        .from('process')
        .select('id, process_name')
        .order('process_name');
      if (data) setProcesses(data);
    };
    loadProcesses();
  }, []);

  // Helper to get date filters
  const getDateFilters = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (filter === 'today') {
      return { start: today.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
    } else if (filter === 'tomorrow') {
      return { start: tomorrow.toISOString().split('T')[0], end: tomorrow.toISOString().split('T')[0] };
    } else if (filter === 'upcoming') {
      return { start: today.toISOString().split('T')[0], end: null };
    } else if (filter === 'past') {
      return { start: null, end: new Date(today.getTime() - 86400000).toISOString().split('T')[0] };
    }
    return { start: dateRange.start, end: dateRange.end };
  };

  // Get farm IDs from context (filtered by organization)
  // FarmContext uses farm_id property, not id
  const farmIds = farms?.map(f => f.farm_id || f.id).filter(id => id !== undefined && id !== null) || [];

  // Load stats (accurate counts from RPC).
  // Block the query while FarmContext is still loading or returned zero farms
  // for a non-admin — otherwise we'd flash stats for every farm on the
  // platform (data-leak race seen on /planting-schedule).
  useEffect(() => {
    const loadStats = async () => {
      if (farmLoading) return;
      if (!isAdmin && farmIds.length === 0) {
        setStats({ total: 0, farms: 0, completed: 0, pending: 0 });
        return;
      }
      try {
        const dates = getDateFilters();
        // Pass farm_ids ONLY for admins (who can see all platform stats by
        // omitting the filter) — for everyone else always pass the scoped list,
        // even if it's large, so the RPC can't silently return platform totals.
        const farmIdsForRpc = isAdmin ? null : farmIds;
        const { data, error } = await supabase.rpc('get_sop_activity_stats', {
          start_date: dates.start,
          end_date: dates.end,
          farm_id_filter: selectedFarmFilter !== 'all' ? parseInt(selectedFarmFilter) : null,
          process_id_filter: selectedProcess !== 'all' ? parseInt(selectedProcess) : null,
          farm_ids: farmIdsForRpc
        });

        if (!error && data && data.length > 0) {
          setStats({
            total: data[0].total_activities || 0,
            farms: data[0].unique_farms || 0,
            completed: data[0].completed || 0,
            pending: data[0].pending || 0
          });
        }
      } catch (err) {
        console.error('Error loading stats:', err);
      }
    };

    loadStats();
  }, [filter, selectedFarmFilter, selectedProcess, dateRange, isAdmin, farmLoading, JSON.stringify(farmIds)]);

  // Load activities with pagination.
  // Guarded against the data-leak race: don't query until FarmContext has
  // loaded and we either know the user is an admin (no farm filter needed)
  // or we have a non-empty farmIds list to scope the query.
  useEffect(() => {
    const loadActivities = async () => {
      if (farmLoading) return;
      if (!isAdmin && farmIds.length === 0) {
        setActivities([]);
        setHasMore(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setPage(0);
      try {
        const dates = getDateFilters();
        let query = supabase
          .from('sop_activity_farm_view')
          .select('*')
          .order('date_activity', { ascending: false });

        if (dates.start) query = query.gte('date_activity', dates.start);
        if (dates.end) query = query.lte('date_activity', dates.end);

        if (selectedFarmFilter !== 'all') {
          query = query.eq('farm_id', parseInt(selectedFarmFilter));
        } else if (!isAdmin) {
          // Always scope non-admin requests to the user's farmIds, regardless
          // of list size. Previously we silently dropped the filter when
          // farmIds.length >= 500, which exposed platform-wide activities.
          const validFarmIds = farmIds.filter(id => typeof id === 'number');
          if (validFarmIds.length > 0) {
            query = query.in('farm_id', validFarmIds);
          } else {
            // Defensive: no valid scope — return nothing rather than everything.
            setActivities([]);
            setHasMore(false);
            setLoading(false);
            return;
          }
        }

        if (selectedProcess !== 'all') {
          query = query.eq('process_id', parseInt(selectedProcess));
        }

        const { data, error } = await query.range(0, PAGE_SIZE - 1);

        if (error) {
          console.error('Error loading activities:', error);
          setActivities([]);
        } else {
          setActivities(data || []);
          setHasMore((data?.length || 0) >= PAGE_SIZE);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, [filter, selectedFarmFilter, selectedProcess, dateRange, isAdmin, farmLoading, JSON.stringify(farmIds)]);

  // Load more activities — same scoping guarantee as the initial load.
  const loadMore = async () => {
    if (!isAdmin && farmIds.length === 0) return;
    const nextPage = page + 1;
    const dates = getDateFilters();
    let query = supabase
      .from('sop_activity_farm_view')
      .select('*')
      .order('date_activity', { ascending: false });

    if (dates.start) query = query.gte('date_activity', dates.start);
    if (dates.end) query = query.lte('date_activity', dates.end);

    if (selectedFarmFilter !== 'all') {
      query = query.eq('farm_id', parseInt(selectedFarmFilter));
    } else if (!isAdmin) {
      const validFarmIds = farmIds.filter(id => typeof id === 'number');
      if (validFarmIds.length > 0) {
        query = query.in('farm_id', validFarmIds);
      } else {
        return;
      }
    }

    if (selectedProcess !== 'all') {
      query = query.eq('process_id', parseInt(selectedProcess));
    }

    const { data, error } = await query.range(nextPage * PAGE_SIZE, (nextPage + 1) * PAGE_SIZE - 1);

    if (!error && data) {
      setActivities(prev => [...prev, ...data]);
      setPage(nextPage);
      setHasMore(data.length >= PAGE_SIZE);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (statusId, statusName) => {
    if (statusId === 2 || statusName?.toLowerCase() === 'completed') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Completed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <ClockIcon className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planting Schedule (SOP)</h1>
          <p className="text-gray-600 text-sm sm:text-base">View and manage farm activities and SOP records</p>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2">
        {['all', 'today', 'tomorrow', 'upcoming', 'past'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, start: e.target.value }));
                setFilter('all');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, end: e.target.value }));
                setFilter('all');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          {/* Farm Filter */}
          <div>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              label="Farm"
            />
          </div>
          {/* Process Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Process</label>
            <select
              value={selectedProcess}
              onChange={(e) => setSelectedProcess(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Processes</option>
              {processes.map(p => (
                <option key={p.id} value={p.id}>
                  {p.process_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Number of Activities</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Number of Farms</p>
              <p className="text-2xl font-bold text-gray-900">{stats.farms}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-3 text-gray-600">Loading activities...</span>
          </div>
        ) : activities.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Process</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.map((activity) => (
                  <tr key={activity.activity_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(activity.date_activity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {activity.process_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {activity.description || activity.dosage_amount || activity.mixture_amount ? (
                        <div>
                          {activity.description && <div>{activity.description}</div>}
                          {(activity.dosage_amount || activity.mixture_amount) && (
                            <div className="text-xs text-gray-500">
                              {activity.dosage_amount ? <>Dosage: <strong>{activity.dosage_amount}</strong></> : null}
                              {activity.mixture_amount ? <> {activity.dosage_amount ? '• ' : ''}Mixture: <strong>{activity.mixture_amount}</strong></> : null}
                            </div>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(activity.status_id, activity.status_name)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {activity.farm_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination info and Load More */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <p className="text-sm text-gray-600">
                Showing {activities.length.toLocaleString()} of {stats.total.toLocaleString()} activities
              </p>
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Load More
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No activities found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlantingSchedule;
